import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "./config";
import {
  DollarSign,
  TrendingUp,
  Activity,
  Wallet,
  Users,
  RefreshCw,
  Clock,
  CreditCard,
  Gift,
  ExternalLink,
  BookOpen,
  BarChart3,
  AlertCircle,
} from "lucide-react";

// XGO FINANCIAL OVERVIEW — eagle-eye / panoramic view of financial performance.
//
// Companion to FinancialSystemGuide.js (which explains what each subsystem IS, statically) —
// this screen shows what each subsystem is DOING RIGHT NOW, from the three platform-wide
// aggregate endpoints that already existed but were not called from anywhere in this dashboard:
// GET /earnings/platform-summary, GET /driver-funds/summary, GET /promotions/wallet/summary.
// Same section colors as FinancialSystemGuide.js on purpose, so a subsystem reads as the same
// color in both places.
//
// v1 scope (2026-09-15): deliberately does NOT include wallet balances across the UW/RW/VW/MW/EW
// folios (financial_top_up) or a Drive to Own portfolio rollup — no aggregate endpoint exists yet
// for either, and financial_top_up's debit/credit sign convention is easy to get backwards (see
// Documentation/FINANCIAL_SYSTEM.md §1) — building that query deserves its own careful pass rather
// than being rushed into this one. See the notice card at the bottom of this page.

const COLOR_CLASSES = {
  green: { bg: "bg-green-50 dark:bg-green-900/20", text: "text-green-600 dark:text-green-400", ring: "border-green-200 dark:border-green-800", bar: "bg-green-500" },
  cyan: { bg: "bg-cyan-50 dark:bg-cyan-900/20", text: "text-cyan-600 dark:text-cyan-400", ring: "border-cyan-200 dark:border-cyan-800", bar: "bg-cyan-500" },
  pink: { bg: "bg-pink-50 dark:bg-pink-900/20", text: "text-pink-600 dark:text-pink-400", ring: "border-pink-200 dark:border-pink-800", bar: "bg-pink-500" },
  blue: { bg: "bg-blue-50 dark:bg-blue-900/20", text: "text-blue-600 dark:text-blue-400", ring: "border-blue-200 dark:border-blue-800", bar: "bg-blue-500" },
};

const currency = (n) =>
  (Number(n) || 0).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });

const number = (n) => (Number(n) || 0).toLocaleString();

const StatTile = ({ label, value, color, icon: Icon }) => {
  const c = COLOR_CLASSES[color];
  return (
    <div className={`rounded-lg border p-4 bg-white dark:bg-gray-800 ${c.ring}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${c.bg}`}>
          <Icon className={c.text} size={16} />
        </div>
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</p>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{value}</p>
    </div>
  );
};

const SectionHeader = ({ title, color, icon: Icon, table }) => {
  const c = COLOR_CLASSES[color];
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${c.bg}`}>
        <Icon className={c.text} size={18} />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
        <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">{table}</p>
      </div>
    </div>
  );
};

// Simple horizontal magnitude comparison — not a stacked-to-100% bar (the underlying amounts are
// each computed as exact per-trip remainders, but SUMMED across many trips their float display
// can be off by a cent or two from the revenue total; a stacked bar would visually imply an exact
// sum that isn't guaranteed at display precision, so this is drawn as independent bars instead).
const MagnitudeBar = ({ label, value, max, color }) => {
  const c = COLOR_CLASSES[color];
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-3">
      <p className="w-40 shrink-0 text-sm text-gray-600 dark:text-gray-300">{label}</p>
      <div className="flex-1 h-3 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
        <div className={`h-full rounded-full ${c.bar}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="w-24 shrink-0 text-right text-sm font-medium text-gray-900 dark:text-white tabular-nums">
        {currency(value)}
      </p>
    </div>
  );
};

const LinkCard = ({ label, description, path }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(path)}
      className="flex items-center justify-between gap-3 w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
    >
      <div>
        <p className="font-medium text-gray-900 dark:text-white">{label}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
      </div>
      <ExternalLink className="text-gray-400 shrink-0" size={18} />
    </button>
  );
};

const FinancialOverview = () => {
  const [platform, setPlatform] = useState(null);
  const [driverFunds, setDriverFunds] = useState(null);
  const [promoWallet, setPromoWallet] = useState(null);
  const [walletBalances, setWalletBalances] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setError(null);
      const [platformRes, fundsRes, promoRes, walletRes] = await Promise.all([
        fetch(`${API_URL}/earnings/platform-summary`),
        fetch(`${API_URL}/driver-funds/summary`),
        fetch(`${API_URL}/promotions/wallet/summary`),
        fetch(`${API_URL}/topUp/wallet-balances-summary`),
      ]);
      const [platformJson, fundsJson, promoJson, walletJson] = await Promise.all([
        platformRes.json(),
        fundsRes.json(),
        promoRes.json(),
        walletRes.json(),
      ]);
      setPlatform(platformJson?.data?.summary || null);
      setDriverFunds(fundsJson?.data || null);
      setPromoWallet(promoJson?.data || null);
      const byFolio = {};
      for (const row of walletJson?.data || []) byFolio[row.folio] = Number(row.total_balance) || 0;
      setWalletBalances(byFolio);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Failed to load financial overview:", err);
      setError("Couldn't reach one or more financial endpoints — figures below may be incomplete.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    // Full-table SUM aggregates on the backend — a longer interval than the 30s used on the
    // trip-level revenue report, to keep this lighter on the DB.
    const interval = setInterval(fetchAll, 60000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const breakdownMax = platform
    ? Math.max(
        platform.total_platform_commission,
        platform.total_fuel_program,
        platform.total_vehicle_rental,
        platform.total_vehicle_maintenance,
        platform.total_driver_earnings
      )
    : 0;

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="text-blue-500" size={28} />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Financial Performance Overview</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl">
            An eagle-eye view across XGO's financial subsystems — platform earnings, driver
            withdrawable funds, and the promotions wallet, from three aggregate endpoints stitched
            into one screen.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <p className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
              <Clock size={14} /> Updated {lastUpdated.toLocaleTimeString()}
            </p>
          )}
          <button
            onClick={fetchAll}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={loading ? "animate-spin" : ""} size={14} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
          <AlertCircle size={16} className="shrink-0" />
          {error}
        </div>
      )}

      {loading && !platform && !driverFunds && !promoWallet && !walletBalances ? (
        <div className="flex items-center justify-center py-24 text-gray-400">
          <RefreshCw className="animate-spin mr-2" size={20} /> Loading financial overview…
        </div>
      ) : (
        <div className="space-y-10">
          {/* Wallet Balances — the actual money currently in each folio, right now */}
          <section>
            <SectionHeader title="Wallet Balances" color="blue" icon={Wallet} table="financial_top_up" />
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <StatTile label="UW — User Wallet" value={currency(walletBalances?.UW)} color="blue" icon={Wallet} />
              <StatTile label="RW — Revenue Wallet" value={currency(walletBalances?.RW)} color="blue" icon={Wallet} />
              <StatTile label="VW — Vendor Wallet" value={currency(walletBalances?.VW)} color="blue" icon={Wallet} />
              <StatTile label="MW — Main Wallet" value={currency(walletBalances?.MW)} color="blue" icon={Wallet} />
              <StatTile label="EW — Escrow Wallet" value={currency(walletBalances?.EW)} color="blue" icon={Wallet} />
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
              Each figure is that folio's real current balance — every entity's latest ledger row,
              summed (UW: every user; RW/MW: XGO's own single wallet; VW: every vendor; EW: every
              active escrow). Deliberately not read from the ledger's own running-total column,
              which was found to drift unreliable on live data — see code comment in{" "}
              <code className="font-mono">cruds/topUp.js</code>'s <code className="font-mono">getWalletBalancesSummary</code>.
            </p>
          </section>

          {/* Earnings Allocation */}
          <section>
            <SectionHeader
              title="Platform Earnings"
              color="green"
              icon={DollarSign}
              table="financial_earnings_allocation_log"
            />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
              <StatTile label="XGO Earnings (Commission)" value={currency(platform?.total_platform_commission)} color="green" icon={DollarSign} />
              <StatTile label="Gross Fare Volume (all trips)" value={currency(platform?.total_platform_revenue)} color="green" icon={Activity} />
              <StatTile label="Total Driver Earnings" value={currency(platform?.total_driver_earnings)} color="green" icon={Users} />
              <StatTile label="Active Drivers" value={number(platform?.active_drivers)} color="green" icon={Users} />
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 -mt-3 mb-5">
              "Gross Fare Volume" is the total of what passengers paid across all trips — most of it
              is paid out (driver earnings, fuel/rental/maintenance), not money XGO keeps. "XGO
              Earnings" (the commission) is the actual revenue that came to the platform.
            </p>
            {platform && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-3">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Revenue components (not a stacked total — see note in source)
                </p>
                <MagnitudeBar label="Commission" value={platform.total_platform_commission} max={breakdownMax} color="green" />
                <MagnitudeBar label="Fuel program" value={platform.total_fuel_program} max={breakdownMax} color="green" />
                <MagnitudeBar label="Vehicle rental" value={platform.total_vehicle_rental} max={breakdownMax} color="green" />
                <MagnitudeBar label="Vehicle maintenance" value={platform.total_vehicle_maintenance} max={breakdownMax} color="green" />
                <MagnitudeBar label="Driver earnings" value={platform.total_driver_earnings} max={breakdownMax} color="green" />
              </div>
            )}
          </section>

          {/* Driver Withdrawable Funds */}
          <section>
            <SectionHeader
              title="Driver Withdrawable Funds"
              color="cyan"
              icon={CreditCard}
              table="financial_withdrawable_funds_balance"
            />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatTile label="Drivers with Funds" value={number(driverFunds?.active_drivers)} color="cyan" icon={Users} />
              <StatTile label="Total Withdrawn" value={currency(driverFunds?.total_withdrawn)} color="cyan" icon={DollarSign} />
              <StatTile label="Pending Withdrawals" value={number(driverFunds?.pending_withdrawals)} color="cyan" icon={Clock} />
              <StatTile label="Pending Amount" value={currency(driverFunds?.pending_amount)} color="cyan" icon={DollarSign} />
            </div>
          </section>

          {/* Promotions Wallet */}
          <section>
            <SectionHeader title="Promotions Wallet" color="pink" icon={Gift} table="financial_promo_wallet" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatTile label="Current Balance" value={currency(promoWallet?.current_balance)} color="pink" icon={Gift} />
              <StatTile label="Total Transferred In" value={currency(promoWallet?.total_transferred)} color="pink" icon={TrendingUp} />
              <StatTile label="Total Spent" value={currency(promoWallet?.total_spent)} color="pink" icon={DollarSign} />
              <StatTile
                label="Credit / Debit Txns"
                value={`${number(promoWallet?.credit_transactions)} / ${number(promoWallet?.debit_transactions)}`}
                color="pink"
                icon={BarChart3}
              />
            </div>
          </section>

          {/* Deeper reports */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Go deeper</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LinkCard
                label="Revenue Breakdown Report"
                description="Per-trip revenue by product line, price range, driver, customer, and time of day."
                path="/revenue-breakdown-report"
              />
              <LinkCard
                label="Financial System Guide"
                description="What each subsystem is, its tables, and the folio / trxn_code glossary."
                path="/financial-system-guide"
              />
            </div>
          </section>

          {/* Known gaps — v1 scope note */}
          <section className="flex items-start gap-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
            <BookOpen className="text-blue-500 shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-blue-800 dark:text-blue-300">
              <p className="font-medium">Not yet on this page (v1 scope, 2026-09-15)</p>
              <p className="mt-1">
                A Drive to Own portfolio rollup — no aggregate endpoint exists yet for it. (Wallet
                balances across the five folios were added 2026-09-15, above.)
              </p>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default FinancialOverview;
